import { useContext, useRef, useState } from "react";
import axios, { CancelTokenSource } from "axios";
import { Alert } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";
import { ButtonLoading } from "go-form";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import handleException from "go-core/api/handleException";
import { ApiError } from "go-core/api/types";
import { CsvImportOverviewFormType, ExportCSVTranslationsApi, ImportOverviewConfig } from "../../../../services/types";
import { CsvContext } from "../../services/context";
import { AttributeProps } from "../../utils/types";
import ImportErrors from "../ImportErrors";
import PreviewItemsTable from "./PreviewItemsTable";

interface Props {
	response: Record<string, any>[];
	importOverviewConfig: ImportOverviewConfig;
	mappingsWithConfig?: CsvImportOverviewFormType;
	translations: ExportCSVTranslationsApi;
	selectedFile?: File;
	attributes: AttributeProps[];
	workingInBackground?: boolean;
	resourceType: string;
	redirectionUrl: string;
	errors: ApiError[];
}

const PreviewItemsWithErrors = ({
	translations: { database_column_name, constraints },
	selectedFile,
	attributes,
	workingInBackground,
	resourceType,
	redirectionUrl,
	response,
	importOverviewConfig,
	errors,
	mappingsWithConfig,
}: Props) => {
	const [loading, setLoading] = useState(false);
	const { t } = useTranslation("lib");
	const shouldDisplayErrorsTable = errors.length > 0;
	const csvImportContext = useContext(CsvContext);
	const history = useHistory();
	const { addFlash, addSuccessFlash } = useFlash();
	const cancelTokenSource = useRef<CancelTokenSource>();
	const importTimeoutRef = useRef<number | undefined>();

	const handleFinishImportTimeout = (jobUid: string, formData: FormData, _headers: Record<string, string>) => {
		if (!csvImportContext.getFinishImportCsvProgress) return;

		importTimeoutRef.current = window.setTimeout(async () => {
			try {
				const CancelToken = axios.CancelToken;
				const newCancelToken = CancelToken.source();
				cancelTokenSource.current = newCancelToken;

				if (!csvImportContext.getFinishImportCsvProgress) return;

				const res = await csvImportContext.getFinishImportCsvProgress(jobUid, resourceType, {
					cancelToken: newCancelToken.token,
				});
				if (res.job_status === "NEW" || res.job_status === "STARTED") {
					setLoading(true);
					handleFinishImportTimeout(res.job_uid, formData, _headers);
				} else {
					setLoading(false);
					window.clearTimeout(importTimeoutRef.current);
					addSuccessFlash(t("common.flash.saved"));
					history.push(redirectionUrl);
				}
			} catch (err) {
				const errs = handleException(err);
				if (errs[0].message === "canceled") return;
				handleError.alert(err, addFlash);
				setLoading(false);
				window.clearTimeout(importTimeoutRef.current);
			}
		}, 1000);
	};

	const finishImport = async () => {
		setLoading(true);
		try {
			const formData = new FormData();
			if (selectedFile) formData.append("csv", selectedFile);
			if (mappingsWithConfig)
				formData.append(
					"config",
					new Blob([JSON.stringify(mappingsWithConfig)], {
						type: "application/json",
					})
				);

			formData.append(
				"attributes",
				new Blob(
					[
						JSON.stringify(
							attributes.filter((f: { domain_field_name: string }) => f.domain_field_name?.length > 0)
						),
					],
					{ type: "application/json" }
				)
			);
			const _headers = { "Content-Type": "multipart/form-data" };
			if (workingInBackground && csvImportContext.finishImportAsyncCsv) {
				const CancelToken = axios.CancelToken;
				const newCancelToken = CancelToken.source();
				cancelTokenSource.current = newCancelToken;
				const res = await csvImportContext.finishImportAsyncCsv(resourceType, formData, _headers, {
					cancelToken: newCancelToken.token,
				});
				handleFinishImportTimeout(res.data.data, formData, _headers);
			} else if (csvImportContext.finishImportCsv) {
				await csvImportContext.finishImportCsv(resourceType, formData, _headers);
				addSuccessFlash(t("common.flash.saved"));
				history.push(redirectionUrl);
			}
		} catch (e) {
			handleError.alert(e, addFlash);
		}
		if (!workingInBackground) {
			setLoading(false);
		}
	};

	if (shouldDisplayErrorsTable) {
		return (
			<>
				<Alert variant="danger">
					{t("go_component.import_csv.constraints.message")}
					{t(`go_component.import_csv.constraints.errors`, { count: errors.length })}
				</Alert>
				<ImportErrors
					errors={errors}
					databaseColumnName={database_column_name}
					constraints={constraints}
					disableTableDynamicHeight
				/>
			</>
		);
	}

	return (
		<>
			<PreviewItemsTable response={response} importOverviewConfig={importOverviewConfig} />
			<div className="sticky-footer">
				<ButtonLoading loading={loading} onClick={finishImport}>
					{t("go_component.field.next.title")}
				</ButtonLoading>
			</div>
		</>
	);
};

export default PreviewItemsWithErrors;
